import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { Button } from '../button';
import { Upload, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { formatCurrencyGBP } from '../../../utils/format';
import { Badge } from '../badge';
import { ScrollArea } from '../scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../table';
import { Alert, AlertDescription } from '../alert';
import { Label } from '../label';

interface CsvInvoiceComparisonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userInvoices: Array<{
    id: number;
    user_id: string;
    userName: string;
    totalEarnings: number;
    submitted: boolean;
  }>;
  users: Array<{
    id: string;
    full_name: string;
  }>;
}

interface CsvRow {
  user_id: string;
  amount: number;
}

interface ComparisonResult {
  user_id: string;
  userName: string;
  csvAmount: number;
  actualAmount: number;
  difference: number;
  percentageDiff: number;
  status: 'match' | 'mismatch' | 'not_found';
  submitted: boolean;
  hasSubmissionMismatch: boolean;
}

export function CsvInvoiceComparisonDialog({
  isOpen,
  onClose,
  userInvoices,
  users,
}: CsvInvoiceComparisonDialogProps) {
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileUpload = useCallback((file: File) => {
    setParseError(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const rows = text.split('\n').filter(row => row.trim());
        
        if (rows.length === 0) {
          setParseError('CSV file is empty');
          return;
        }

        // Parse headers
        const headers = rows[0].toLowerCase().split(',').map(h => h.trim());
        const user_idIndex = headers.indexOf('user_id');
        const amountIndex = headers.indexOf('amount');

        if (user_idIndex === -1 || amountIndex === -1) {
          setParseError('CSV must contain "user_id" and "amount" columns');
          return;
        }

        // Parse data rows
        const parsedData: CsvRow[] = [];
        for (let i = 1; i < rows.length; i++) {
          const columns = rows[i].split(',').map(c => c.trim());
          if (columns.length > user_idIndex && columns.length > amountIndex) {
            const user_id = columns[user_idIndex];
            const amount = parseFloat(columns[amountIndex]);
            
            if (user_id && !isNaN(amount)) {
              parsedData.push({ user_id, amount });
            }
          }
        }

        performComparison(parsedData);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        setParseError('Failed to parse CSV file. Please ensure it is properly formatted.');
      }
    };

    reader.onerror = () => {
      setParseError('Failed to read file');
    };

    reader.readAsText(file);
  }, [userInvoices, users]);

  const performComparison = (data: CsvRow[]) => {
    const results: ComparisonResult[] = [];

    // Create a map of user invoices by user_id for quick lookup
    const invoiceMap = new Map(userInvoices.map(inv => [inv.user_id, inv]));

    // Process each CSV row
    data.forEach(row => {
      const invoice = invoiceMap.get(row.user_id);
      const user = users.find(u => u.id === row.user_id);

      if (invoice) {
        const difference = Math.abs(invoice.totalEarnings - row.amount);
        const percentageDiff = invoice.totalEarnings > 0 
          ? (difference / invoice.totalEarnings) * 100 
          : 0;

        results.push({
          user_id: row.user_id,
          userName: user?.full_name || 'Unknown User',
          csvAmount: row.amount,
          actualAmount: invoice.totalEarnings,
          difference,
          percentageDiff,
          status: difference < 0.01 ? 'match' : 'mismatch',
          submitted: invoice.submitted,
          hasSubmissionMismatch: !invoice.submitted,
        });
      } else {
        results.push({
          user_id: row.user_id,
          userName: user?.full_name || 'Unknown User',
          csvAmount: row.amount,
          actualAmount: 0,
          difference: row.amount,
          percentageDiff: 100,
          status: 'not_found',
          submitted: false,
          hasSubmissionMismatch: false,
        });
      }
    });

    // Sort results by status (submission mismatches first, then amount mismatches) and then by difference
    results.sort((a, b) => {
      // Submission mismatches come first
      if (a.hasSubmissionMismatch && !b.hasSubmissionMismatch) return -1;
      if (!a.hasSubmissionMismatch && b.hasSubmissionMismatch) return 1;
      
      // Then amount mismatches
      if (a.status === 'mismatch' && b.status !== 'mismatch') return -1;
      if (a.status !== 'mismatch' && b.status === 'mismatch') return 1;
      if (a.status === 'not_found' && b.status === 'match') return -1;
      if (a.status === 'match' && b.status === 'not_found') return 1;
      return b.difference - a.difference;
    });

    setComparisonResults(results);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(file => file.type === 'text/csv' || file.name.endsWith('.csv'));
    
    if (csvFile) {
      handleFileUpload(csvFile);
    } else {
      setParseError('Please upload a CSV file');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const resetComparison = () => {
    setComparisonResults([]);
    setParseError(null);
  };

  const getStatusBadge = (status: ComparisonResult['status']) => {
    switch (status) {
      case 'match':
        return <Badge variant="default" className="bg-brand-green">Match</Badge>;
      case 'mismatch':
        return <Badge variant="destructive">Mismatch</Badge>;
      case 'not_found':
        return <Badge variant="secondary">Not Found</Badge>;
    }
  };

  const getSummaryStats = () => {
    const total = comparisonResults.length;
    const matches = comparisonResults.filter(r => r.status === 'match').length;
    const mismatches = comparisonResults.filter(r => r.status === 'mismatch').length;
    const notFound = comparisonResults.filter(r => r.status === 'not_found').length;
    const notSubmitted = comparisonResults.filter(r => r.hasSubmissionMismatch).length;
    
    return { total, matches, mismatches, notFound, notSubmitted };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Compare CSV Invoice Amounts</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {comparisonResults.length === 0 ? (
            <div
              className={`
                border-2 border-dashed rounded-lg p-8 text-center
                ${isDragging ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-border'}
                transition-colors duration-200
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Upload CSV File</p>
              <p className="text-sm text-muted-foreground mb-4">
                Drag and drop a CSV file here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                CSV must contain "user_id" and "amount" columns
              </p>
              <Label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                <Button variant="outline" asChild>
                  <span>Choose File</span>
                </Button>
              </Label>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  {(() => {
                    const stats = getSummaryStats();
                    return (
                      <>
                        <span>Total: {stats.total}</span>
                        <span className="text-brand-green">
                          <CheckCircle2 className="inline h-4 w-4 mr-1" />
                          Matches: {stats.matches}
                        </span>
                        <span className="text-brand-red">
                          <AlertCircle className="inline h-4 w-4 mr-1" />
                          Mismatches: {stats.mismatches}
                        </span>
                        {stats.notFound > 0 && (
                          <span className="text-muted-foreground">
                            Not Found: {stats.notFound}
                          </span>
                        )}
                        {stats.notSubmitted > 0 && (
                          <span className="text-brand-gold">
                            <X className="inline h-4 w-4 mr-1" />
                            Not Submitted: {stats.notSubmitted}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetComparison}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Reset
                </Button>
              </div>

              <ScrollArea className="h-[400px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">CSV Amount</TableHead>
                      <TableHead className="text-right">Actual Amount</TableHead>
                      <TableHead className="text-right">Difference</TableHead>
                      <TableHead className="text-right">% Diff</TableHead>
                      <TableHead>Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparisonResults.map((result) => (
                      <TableRow
                        key={result.user_id}
                        className={
                          result.hasSubmissionMismatch
                            ? 'bg-brand-gold dark:bg-brand-dark/20'
                            : result.status === 'mismatch' 
                            ? 'bg-brand-red dark:bg-brand-dark/20' 
                            : result.status === 'not_found'
                            ? 'bg-brand-dark dark:bg-brand-dark/20'
                            : ''
                        }
                      >
                        <TableCell>{getStatusBadge(result.status)}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{result.userName}</div>
                            <div className="text-xs text-muted-foreground">{result.user_id}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrencyGBP(result.csvAmount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrencyGBP(result.actualAmount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {result.difference > 0.01 && (
                            <span className="text-brand-red">
                              {formatCurrencyGBP(result.difference)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {result.percentageDiff > 0.01 && (
                            <span className="text-brand-red">
                              {result.percentageDiff.toFixed(1)}%
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.hasSubmissionMismatch ? (
                            <Badge variant="destructive" className="bg-brand-gold">
                              Not Submitted
                            </Badge>
                          ) : result.status !== 'not_found' ? (
                            <Badge variant="default" className="bg-brand-green">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Submitted
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </>
          )}

          {parseError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{parseError}</AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}